import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { Search, Plus, User, LogOut, BookOpen, GraduationCap, Menu, Bell, Shield, ChevronDown, MessageCircle } from 'lucide-react';
import { lazy, Suspense, useState } from 'react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const NotificationBell = lazy(() => import('@/components/notifications/NotificationBell').then(m => ({ default: m.NotificationBell })));
import { useUnreadMessageCount } from '@/hooks/useMessages';

export function Header() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: unreadMessages } = useUnreadMessageCount();

  const { data: isAdmin } = useQuery({
    queryKey: ['is-admin', user?.id],
    queryFn: async () => {
      const { data } = await supabase.rpc('has_role', { _user_id: user!.id, _role: 'admin' });
      return data === true;
    },
    enabled: !!user,
  });

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" aria-label="Learn Around Town home">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg gradient-primary">
            <GraduationCap className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-serif text-xl font-medium hidden sm:block">Learn Around Town</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          <Link to="/browse" className="text-sm font-medium text-muted-foreground hover:text-accent transition-colors">
            Browse Classes
          </Link>
          <Link to="/earn-money" className="text-sm font-medium text-muted-foreground hover:text-accent transition-colors">
            Earn Money
          </Link>
          <Link to="/faqs" className="text-sm font-medium text-muted-foreground hover:text-accent transition-colors">
            FAQs
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-accent transition-colors outline-none">
              More <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/suggestions">Suggestions</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/about">About</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/contact">Contact</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="hidden sm:flex" asChild>
            <Link to="/browse" aria-label="Search classes">
              <Search className="h-5 w-5" />
            </Link>
          </Button>

          {user ? (
            <>
              <Button variant="outline" size="sm" className="hidden sm:flex gap-2" asChild>
                <Link to="/teach">
                  <Plus className="h-4 w-4" />
                  Teach a Class
                </Link>
              </Button>


              <Suspense fallback={null}>
                <NotificationBell />
              </Suspense>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name || 'User'} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {getInitials(profile?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <div className="flex items-center gap-2 p-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url || undefined} />
                      <AvatarFallback>{getInitials(profile?.full_name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{profile?.full_name || 'User'}</span>
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer">
                      <BookOpen className="mr-2 h-4 w-4" />
                      My Learning
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/instructor" className="cursor-pointer">
                      <GraduationCap className="mr-2 h-4 w-4" />
                      Instructor Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/notifications" className="cursor-pointer">
                      <Bell className="mr-2 h-4 w-4" />
                      Notifications
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer">
                        <Shield className="mr-2 h-4 w-4" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild className="hidden sm:flex">
                <Link to="/auth">Sign In</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/auth?mode=signup">Get Started</Link>
              </Button>
            </>
          )}

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <nav className="flex flex-col gap-4 mt-8">
                <Link 
                  to="/earn-money" 
                  className="text-lg font-medium hover:text-accent transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Earn Money
                </Link>
                <Link 
                  to="/browse" 
                  className="text-lg font-medium hover:text-accent transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Browse Classes
                </Link>
                <Link 
                  to="/faqs" 
                  className="text-lg font-medium hover:text-accent transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  FAQs
                </Link>
                <Link 
                  to="/about" 
                  className="text-lg font-medium hover:text-accent transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  About
                </Link>
                <Link 
                  to="/contact" 
                  className="text-lg font-medium hover:text-accent transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Contact
                </Link>
                <Link 
                  to="/suggestions" 
                  className="text-lg font-medium hover:text-accent transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Suggestions
                </Link>
                {user && (
                  <>
                    <div className="border-t my-2" />
                    <Link 
                      to="/dashboard" 
                      className="text-lg font-medium hover:text-primary transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      My Learning
                    </Link>
                    <Link 
                      to="/teach" 
                      className="text-lg font-medium hover:text-primary transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      Teach a Class
                    </Link>
                    <Link 
                      to="/instructor" 
                      className="text-lg font-medium hover:text-primary transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      Instructor Dashboard
                    </Link>
                    {isAdmin && (
                      <Link 
                        to="/admin" 
                        className="text-lg font-medium hover:text-primary transition-colors"
                        onClick={() => setMobileOpen(false)}
                      >
                        Admin
                      </Link>
                    )}
                  </>
                )}
                <div className="border-t my-2" />
                {user ? (
                  <button
                    onClick={() => { setMobileOpen(false); handleSignOut(); }}
                    className="text-lg font-medium text-destructive hover:text-destructive/80 transition-colors text-left"
                  >
                    Sign Out
                  </button>
                ) : (
                  <>
                    <Link to="/auth" className="text-lg font-medium hover:text-accent transition-colors" onClick={() => setMobileOpen(false)}>
                      Sign In
                    </Link>
                    <Link to="/auth?mode=signup" className="text-lg font-medium hover:text-accent transition-colors" onClick={() => setMobileOpen(false)}>
                      Get Started
                    </Link>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
